import { useDropzone, ErrorCode, type FileRejection } from "react-dropzone";
import { useCallback } from "react";
import { isValidImageType } from "@/lib/utils";
import { MAX_IMAGE_SIZE_MB } from "@/lib/image-utils";
import { toast } from "react-toastify";
import {
  ImageCropper,
  ImageCropperHandle,
} from "@/components/image/ImageCropper";
import UploadDropArea from "@/components/ui/UploadDropArea";

const IMAGE_ACCEPT = {
  "image/jpeg": [],
  "image/png": [],
  "image/jpg": [],
};

type UploadImagePanelProps = {
  imageSrc: string | null;
  onSelectImage: (dataUrl: string) => void;
  onCropComplete: (file: File) => void;
  cropperRef: React.RefObject<ImageCropperHandle | null>;
  circular?: boolean;
};

export default function UploadImagePanel({
  imageSrc,
  onSelectImage,
  onCropComplete,
  cropperRef,
  circular,
}: UploadImagePanelProps) {
  // Circular crops are profile photos.
  const subject = circular ? "photo" : "artwork";

  const onDrop = useCallback(
    (acceptedFiles: File[], rejected: FileRejection[]) => {
      const selectedFile = acceptedFiles[0];
      if (!selectedFile) {
        const codes = rejected.flatMap((r) => r.errors.map((e) => e.code));
        toast.error(
          codes.includes(ErrorCode.TooManyFiles)
            ? "Drop one image at a time."
            : codes.includes(ErrorCode.FileTooLarge)
              ? `Image must be under ${MAX_IMAGE_SIZE_MB}MB.`
              : "Please upload a valid image.",
        );
        return;
      }
      if (!isValidImageType(selectedFile.type)) {
        toast.error("Please upload a valid image.");
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        onSelectImage(reader.result as string);
      };
      reader.readAsDataURL(selectedFile);
    },
    [onSelectImage],
  );

  const dropzone = useDropzone({
    onDrop,
    accept: IMAGE_ACCEPT,
    maxFiles: 1,
    maxSize: MAX_IMAGE_SIZE_MB * 1024 * 1024,
  });

  return (
    <>
      {imageSrc ? (
        <ImageCropper
          ref={cropperRef}
          src={imageSrc}
          onComplete={onCropComplete}
          onChangeImage={onSelectImage}
          circular={circular}
        />
      ) : (
        <UploadDropArea
          dropzone={dropzone}
          title={`Drag your ${subject} here or`}
          dropTitle={`Drop your ${subject} here`}
          requirements={
            circular
              ? `JPG or PNG, under ${MAX_IMAGE_SIZE_MB}MB.`
              : `Suggested size: 3000x3000px. JPG or PNG, under ${MAX_IMAGE_SIZE_MB}MB.`
          }
          className="min-h-[400px]"
          testId="image-upload-dropzone"
          browseTestId="image-upload-browse"
        />
      )}
    </>
  );
}
